// MEO landmark ads (Phase 1, Step 4-1 partial / B-1, 2026-05-01):
// SettingFormItem に追加する MEO 商業ポストカード用フィールド集合。
// すべて optional。クーポン (item_type=COUPON=1) として運用される際に意味を持つ。
//
// 親側: form (UseFormReturn) を渡す。submit 時に form.getValues() で読み出し payload 化。

import { FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/common/Select';
import { useStore } from '@/hooks/useStore';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

const FORTUNE_OPTIONS = [
    { value: '', label: '(未設定)' },
    { value: '1', label: '大吉' },
    { value: '2', label: '中吉' },
    { value: '3', label: '小吉' },
    { value: '4', label: '末吉' },
    { value: '5', label: '凶' }
];

const MOTIF_OPTIONS = [
    { value: '', label: '(未設定)' },
    { value: 'whaleshark', label: 'ジンベエザメ' },
    { value: 'turtle', label: 'アオウミガメ' },
    { value: 'yambaru_kuina', label: 'ヤンバルクイナ' },
    { value: 'akashobin', label: 'アカショウビン' },
    { value: 'mangrove', label: '月夜のマングローブ' }
];

// Radix Select の sentinel ('' は禁止)
const NONE_SENTINEL = '__none__';

interface Props {
    form: any
}

export default observer(function ItemMeoFieldsSection({ form }: Props) {
    const {
        landmarkStore: { landmarks, list: fetchLandmarks }
    } = useStore();

    useEffect(() => {
        // landmark dropdown 用に取得 (page=1, limit=100)
        flowResult(fetchLandmarks({ page: 1, limit: 100 }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className='border border-background-6 !rounded-[5px] pb-4 mt-4'>
            <div className='px-6 py-3 text-[15px] font-semibold bg-background-6 rounded-t-sm'>
                MEO 商業ポストカード設定 (任意 / アイテム種別がクーポンの時のみ意味を持つ)
            </div>

            {/* F4 (2026-05-06): backend (drawOmikuji) は shake 時に確率分布で fortune を抽選 →
                FORTUNE_MOTIF_MAP で motif を自動決定する。ここの fortune / motif 設定は集計用 / 候補絞り込み用で、
                enduser 表示には反映されない (= 25 セル運用整合性のため)。
                spec design/meo-landmark-ads-spec.md §1 (drawOmikuji)、§付録 A (motif/fortune 対応表) 参照。 */}
            <p className='px-6 mt-3 text-xs text-muted-foreground'>
                ※ enduser に表示される画像 / fortune は、shake 時に backend が抽選する fortune から自動決定されます (大吉=ジンベエザメ、中吉=アオウミガメ、小吉=ヤンバルクイナ、末吉=アカショウビン、凶=月夜のマングローブ)。
                <br />
                ここでの設定は
                {' '}
                <strong>候補絞り込み (= 当該 fortune に一致する item を優先選出)</strong>
                {' '}
                と
                {' '}
                <strong>集計表示</strong>
                {' '}
                のためのみです。
            </p>

            <div className='mt-4 px-6 grid grid-cols-2 gap-4'>
                <FormField
                    control={form.control}
                    name='fortune'
                    render={({ field }) => (
                        <FormItem>
                            <Label>占い階層 (fortune)</Label>
                            <FormControl>
                                <Select
                                    onValueChange={v => field.onChange(v === NONE_SENTINEL ? '' : v)}
                                    value={field.value ? String(field.value) : NONE_SENTINEL}
                                >
                                    <SelectTrigger className='mt-2'>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {FORTUNE_OPTIONS.map(o => (
                                            <SelectItem
                                                key={o.value || 'none'}
                                                value={o.value || NONE_SENTINEL}
                                                className='hover:cursor-pointer'
                                            >
                                                {o.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name='motif'
                    render={({ field }) => (
                        <FormItem>
                            <Label>モチーフ (motif)</Label>
                            <FormControl>
                                <Select
                                    onValueChange={v => field.onChange(v === NONE_SENTINEL ? '' : v)}
                                    value={typeof field.value === 'string' && field.value ? field.value : NONE_SENTINEL}
                                >
                                    <SelectTrigger className='mt-2'>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MOTIF_OPTIONS.map(o => (
                                            <SelectItem
                                                key={o.value || 'none'}
                                                value={o.value || NONE_SENTINEL}
                                                className='hover:cursor-pointer'
                                            >
                                                {o.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name='landmarkId'
                    render={({ field }) => (
                        <FormItem>
                            <Label>ランドマーク (landmark_id)</Label>
                            <FormControl>
                                <Select
                                    onValueChange={v => field.onChange(v === NONE_SENTINEL ? '' : v)}
                                    value={field.value ? String(field.value) : NONE_SENTINEL}
                                >
                                    <SelectTrigger className='mt-2'>
                                        <SelectValue placeholder='選択' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={NONE_SENTINEL} className='hover:cursor-pointer'>
                                            (未設定)
                                        </SelectItem>
                                        {landmarks.map((lm: any) => (
                                            <SelectItem
                                                key={lm.id}
                                                value={String(lm.id)}
                                                className='hover:cursor-pointer'
                                            >
                                                {`#${lm.id} ${lm.name} (${lm.category ?? '-'})`}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name='advertiserUserId'
                    render={({ field }) => (
                        <FormItem>
                            <Label>事業者 user id (advertiser_user_id)</Label>
                            <FormControl>
                                <Input
                                    type='number'
                                    placeholder='BUSINESS user の id (空欄=未指定)'
                                    {...field}
                                    value={field.value ?? ''}
                                    className='mt-2'
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name='validFrom'
                    render={({ field }) => (
                        <FormItem>
                            <Label>有効期間 from (valid_from)</Label>
                            <FormControl>
                                <Input
                                    type='date'
                                    {...field}
                                    value={field.value ?? ''}
                                    className='mt-2'
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name='validTo'
                    render={({ field }) => (
                        <FormItem>
                            <Label>有効期間 to (valid_to)</Label>
                            <FormControl>
                                <Input
                                    type='date'
                                    {...field}
                                    value={field.value ?? ''}
                                    className='mt-2'
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name='maxCollections'
                    render={({ field }) => (
                        <FormItem>
                            <Label>配布数 (最大取得数)</Label>
                            <FormControl>
                                <Input
                                    type='number'
                                    placeholder='空欄=無制限 / 1=1枚限定'
                                    {...field}
                                    value={field.value ?? ''}
                                    className='mt-2'
                                />
                            </FormControl>
                            <p className='text-[13px] text-text-gray-1 mt-1'>
                                取得数がこの数に達すると、全ユーザーの地図から消えます（イベントの実物1枚配置は「1」）。空欄は無制限。
                            </p>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
        </div>
    );
});
